/**
 * DSCConfiguratorPage.jsx
 *
 * Drill String Configurator page shell — renders the TH or DTH layout based
 * on the `?type=` URL query (or `:type` path param).
 *
 * Bare URL handling: when no `?type=` is supplied (typically from an old
 * bookmark or a direct paste of `/drill-string-configurator`), the page
 * redirects to `/tool-finder` where the TH/DTH entry tiles live. There is
 * intentionally NO intermediate selection page between hub and DSC
 * (Vandana, 20 May 2026 walkthrough — design rule).
 *
 * Routes registered in src/App.jsx:
 *   <Route path="/drill-string-configurator"        element={<DSCConfiguratorPage />} />
 *   <Route path="/drill-string-configurator/:type"  element={<DSCConfiguratorPage />} />
 *
 * @module pages/DSCConfiguratorPage
 * @since 2026-05-20 (Group E SP1.5; picker removed in post-walkthrough UX batch)
 */

import { useMemo, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import Breadcrumbs from '@components/layout/Breadcrumbs';
import ContactHelpBanner from '@components/contact/ContactHelpBanner';
import { DSCProvider } from '@contexts/DSCContext';
import {
  loadAllData,
  loadRockDrillManufacturers,
  loadRockDrills,
} from '@services/dataService';
import THDSCLayout from '@components/dsc/THDSCLayout';
import DTHDSCLayout from '@components/dsc/DTHDSCLayout';

/**
 * Flatten the grouped-by-subcategory product map returned by
 * dataService.loadAllData into a single array.
 */
function flattenProductMap(grouped) {
  if (!grouped || typeof grouped !== 'object') return [];
  const out = [];
  for (const arr of Object.values(grouped)) {
    if (Array.isArray(arr)) out.push(...arr);
  }
  return out;
}

/**
 * Resolve product line from URL — prefers ?type= query param, falls back
 * to :type path param.
 *
 * Accepted values:
 *   'top-hammer'    -> 'TH'
 *   'dth'           -> 'DTH'
 *   'down-the-hole' -> 'DTH'  (legacy Apex back-compat)
 */
function resolveProductLine(search, pathParam) {
  const params = new URLSearchParams(search || '');
  const raw = (params.get('type') || pathParam || '').toLowerCase().trim();
  if (raw === 'top-hammer' || raw === 'th') return { productLine: 'TH' };
  if (raw === 'dth' || raw === 'down-the-hole') return { productLine: 'DTH' };
  return { productLine: null };
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function DSCConfiguratorPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { type: pathType } = useParams();

  const { productLine } = useMemo(
    () => resolveProductLine(location.search, pathType),
    [location.search, pathType]
  );

  const [thProducts, setThProducts] = useState([]);
  const [dthProducts, setDthProducts] = useState([]);
  const [rockDrillManufacturers, setRockDrillManufacturers] = useState(null);
  const [rockDrills, setRockDrills] = useState(null);
  useEffect(() => {
    let cancelled = false;
    // Products (per-line cascade input).
    loadAllData()
      .then(({ products, productsDTH }) => {
        if (cancelled) return;
        setThProducts(flattenProductMap(products));
        setDthProducts(flattenProductMap(productsDTH));
      })
      .catch(() => {});
    // Rock-drill catalogs
    loadRockDrillManufacturers()
      .then((data) => { if (!cancelled) setRockDrillManufacturers(data); })
      .catch(() => {});
    loadRockDrills()
      .then((data) => { if (!cancelled) setRockDrills(data); })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Bare URL → redirect to /tool-finder.
  useEffect(() => {
    if (!productLine) {
      navigate('/tool-finder', { replace: true });
    }
  }, [productLine, navigate]);

  const lineProducts = useMemo(() => {
    if (!productLine) return [];
    return productLine === 'TH' ? thProducts : dthProducts;
  }, [productLine, thProducts, dthProducts]);

  if (!productLine) return null;

  return (
    <DSCProvider
      productLine={productLine}
      initialUrlSearch={location.search}
      products={lineProducts}
      rockDrillManufacturers={rockDrillManufacturers}
      rockDrills={rockDrills}
    >
      <div className="sandvik-container py-6">
        <Breadcrumbs />
        {productLine === 'TH' && (
          <h1 className="sr-only">Top hammer Drill string configurator</h1>
        )}
        {productLine === 'TH' ? <THDSCLayout /> : <DTHDSCLayout />}
        <ContactHelpBanner
          title="Need help configuring your drill string?"
          topics={[
            { topicId: 'product-info', label: 'Technical support', icon: 'engineering' },
            { topicId: 'missing-rig', label: "Can't find rig/model", icon: 'construction' },
            { topicId: 'missing-drill', label: "Can't find rock drill", icon: 'handyman' },
          ]}
        />
      </div>
    </DSCProvider>
  );
}
